<template>
  <FamilyDrawerHeader sub-title="Identifiera dig för extra mobilabonnemang" />
  <div class="px-16 md:px-24">
    <IconButton
      aria-label="Gå tillbaka"
      icon-classes="text-[2.4rem] self-baseline"
      icon-name="arrow-left"
      @click="familyStore.showFamilyDrawerPdpStart()"
      @keyup.enter.once.self.prevent="familyStore.showFamilyDrawerPdpStart()"
      @keyup.space.once.self.prevent="familyStore.showFamilyDrawerPdpStart()"
    />
  </div>
  <div class="px-16 md:px-24 pt-8 md:pt-16 pb-32 mb-32">
    <p class="pb-16">
      Jag har redan ett 20 GB abonnemang eller högre som jag vill koppla ett
      familjeabonnemang till.
    </p>

    <LazyAppTestBankIDReplacement
      v-if="
        isTestEnvironment() && familyStore.ui.bankIdTestPassthroughIsSelected
      "
      :is-loading-passthrough="familyStore.ui.bankIdTestPassthroughIsSubmitting"
      :accordion-default-open="true"
      :manual-error-message="manualErrorMessage ?? undefined"
      @on-complete="onIdentified($event)"
    />

    <AppBankID
      v-else
      :open-immediately="true"
      :custom-error-message="
        familyStore.existingCustomer.isIdentified &&
        !familyStore.existingCustomer.hasPlans
          ? 'Vi kan tyvärr inte se att du har ett huvudabonnemang som kan lägga till Halebop familj. Kontakta vår support så hjälper vi dig!'
          : ''
      "
      :store="'Familydrawer'"
      @on-complete="onIdentified($event)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { BankIdOnIdentifiedEvent } from '~/models/bankid/bankIdOnIdentifiedEvent.model';
import type { HalebopRequestErrorData } from '~/repository/factory';
import { isTestEnvironment } from '~/utils/general/isTestEnvironment';
const { $api } = useNuxtApp();

const familyStore = useFamilyStore();
const lastFetchGotError: Ref<boolean> = ref(false);

const manualErrorMessage = computed(() => {
  if (
    familyStore.existingCustomer.isIdentified &&
    !familyStore.existingCustomer.hasPlans &&
    !familyStore.ui.bankIdTestPassthroughIsSubmitting
  ) {
    return `Det finns inget huvudabonnemang som är godkännt för Halebop familj.
        Testa med en annan testperson.`;
  }

  if (lastFetchGotError.value) {
    return 'Det gick tyvärr inte att kolla mot systemet. Vänligen prova igen.';
  }

  return null;
});

const onIdentified = async (eventData: BankIdOnIdentifiedEvent) => {
  const { bankIdCollectResponse, isUsingBankIdReplacement, testPersonaSsn } =
    eventData;

  lastFetchGotError.value = false;

  if (
    isUsingBankIdReplacement !== true &&
    bankIdCollectResponse?.StatusDescription !== 'COMPLETE'
  ) {
    return;
  }

  try {
    if (isUsingBankIdReplacement && testPersonaSsn) {
      familyStore.ui.bankIdTestPassthroughIsSubmitting = true;
      await $api.CustomerModule.setSsnForTestEnvironmentBankIdPassthrough(
        testPersonaSsn,
      );
    }

    await familyStore.getCustomerPlansEligibleForFamilyConnections();
  } catch (error) {
    lastFetchGotError.value = true;

    if (
      isNuxtError<HalebopRequestErrorData>(error) &&
      error.data?.errorCodeDetail === 'SsnAuthDifferentSsn'
    ) {
      // Don't mind the different ssn authentication here
      await familyStore.getCustomerPlansEligibleForFamilyConnections();
    }

    if (isNuxtError<HalebopRequestErrorData>(error) && error.message) {
      logError(error);
    }
  } finally {
    familyStore.ui.bankIdTestPassthroughIsSubmitting = false;
  }
};

onMounted(() => {
  familyStore.ui.bankIdTestPassthroughIsSubmitting = false;
});
</script>

<style scoped></style>
